import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';

export interface Deal {
  id: string;
  title: string;
  description: string;
  deal_value: number;
  deal_type: string;
  status: string;
  priority: string;
  estimated_completion_date: string;
  actual_completion_date: string;
  talent_profile_id: string;
  agent_profile_id: string;
  created_at: string;
  updated_at: string;
  tasks?: DealTask[];
  progress?: DealProgress[];
}

export interface DealTask {
  id: string;
  deal_id: string;
  title: string;
  description: string;
  status: string;
  assigned_to: string;
  start_date?: string;
  due_date: string;
  completion_date: string;
  progress_percentage: number;
  depends_on_task_id: string;
  payment_amount?: number;
  payment_status?: 'unpaid' | 'due' | 'paid';
  paid_at?: string;
  paid_by_profile_id?: string;
  payment_transaction_id?: string;
  created_at: string;
  updated_at: string;
}

export interface DealProgress {
  id: string;
  deal_id: string;
  status_change: string;
  notes: string;
  created_by_profile_id: string;
  created_at: string;
}

export const useDeals = (selectedAthleteId?: string) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, accountType } = useUser();

  const fetchDeals = async () => {
    if (!currentUser?.id) return;

    setLoading(true);
    setError(null);
    try {
      let query = supabase.from('deals').select(`
        *,
        tasks:deal_tasks(*),
        progress:deal_progress(*)
      `);

      if (accountType === 'agent' && selectedAthleteId && selectedAthleteId !== 'all') {
        // For agents viewing specific talent's deals
        const { data: talentData } = await supabase
          .from('talents')
          .select('profile_id')
          .eq('id', selectedAthleteId)
          .maybeSingle();
        
        if (talentData?.profile_id) {
          query = query.eq('talent_profile_id', talentData.profile_id);
        }
      } else if (accountType === 'agent' && (selectedAthleteId === 'all' || !selectedAthleteId)) {
        // For agents viewing all linked talents' deals
        const { data: agent } = await supabase
          .from('agents')
          .select('id')
          .eq('profile_id', currentUser.id)
          .maybeSingle();
        
        if (agent) {
          const { data: linkedTalents } = await supabase
            .from('talents')
            .select('profile_id')
            .eq('agent_id', agent.id);
          
          if (linkedTalents && linkedTalents.length > 0) {
            const profileIds = linkedTalents.map(a => a.profile_id);
            query = query.in('talent_profile_id', profileIds);
          } else {
            // Agent has no linked talents, return empty result
            setDeals([]);
            return;
          }
        } else {
          // No agent profile found, return empty result
          setDeals([]);
          return;
        }
      } else if (accountType === 'talent') {
        // Talent's own deals
        query = query.eq('talent_profile_id', currentUser.id);
      } else if (accountType === 'brand') {
        // For brands, we'll show deals where they might be involved
        // For now, let's show all deals (this can be refined later)
        // In a real app, you'd filter by brand-specific criteria
        query = query; // Show all deals for brands (can be refined)
      } else {
        // Default: talent's own deals
        query = query.eq('talent_profile_id', currentUser.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      setDeals((data || []) as Deal[]);
    } catch (err) {
      console.error('Error fetching deals:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      setError('Failed to load deals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, [currentUser?.id, selectedAthleteId]);

  const updateTaskStatus = async (taskId: string, status: string, progressPercentage?: number) => {
    const updates: any = { status };
    if (progressPercentage !== undefined) {
      updates.progress_percentage = progressPercentage;
    }
    if (status === 'completed') {
      updates.completion_date = new Date().toISOString().split('T')[0];
    }

    const { error } = await supabase
      .from('deal_tasks')
      .update(updates)
      .eq('id', taskId);

    if (error) throw error;
    await fetchDeals(); // Refresh data
  };

  const addDealProgress = async (dealId: string, statusChange: string, notes?: string) => {
    if (!currentUser?.id) return;
    
    const { error } = await supabase
      .from('deal_progress')
      .insert({
        deal_id: dealId,
        status_change: statusChange,
        notes: notes,
        created_by_profile_id: currentUser.id,
      });

    if (error) throw error;
    await fetchDeals(); // Refresh data
  };

  // Calculate overall deal progress
  const calculateDealProgress = (deal: Deal): number => {
    if (!deal.tasks || deal.tasks.length === 0) return 0;
    const totalProgress = deal.tasks.reduce((sum, task) => sum + task.progress_percentage, 0);
    return Math.round(totalProgress / deal.tasks.length);
  };

  return {
    deals,
    loading,
    error,
    fetchDeals,
    updateTaskStatus,
    addDealProgress,
    calculateDealProgress,
  };
};